import React from "react";
import { Icon } from "@/components/atoms";
import ShimmerTableRow from "@/components/atoms/ShimmerTableRow";
import DataTableBody from "./DataTableBody";
import DateRangeCustomInput from "./DateRangeCustomInput";
import SingleDateCustomInput from "./SingleDateCustomInput";
import DataTableFilterRow from "./DataTableFilterRow";
import DataTablePagination from "./DataTablePagination";
import DataTableHead from "./DataTableHead";
import DataTableToolbar from "./DataTableToolbar";
import SelectionSummary from "./SelectionSummary";
import EmptyState from "@/components/molecules/EmptyState/EmptyState";
import { useCustomDataTable } from '@/hooks/useCustomDataTable-hook';

import {
  Paper,
  Table,
  TableContainer,
  TableHead,
} from "@mui/material";
import 'react-datepicker/dist/react-datepicker.css';
import "./CustomDataTable.scss";



// Wrapper components moved out of render to avoid redefining on each render
function DateRangeCustomInputWrapper(props) {
  // dateRanges and setOpenDatePicker will be injected via closure from CustomDataTable
  // We'll assign these as variables below
  return (
    <DateRangeCustomInput
      {...props}
      dateRanges={DateRangeCustomInputWrapper.dateRanges}
      setOpenDatePicker={DateRangeCustomInputWrapper.setOpenDatePicker}
    />
  );
}

function SingleDateCustomInputWrapper(props) {
  return (
    <SingleDateCustomInput
      {...props}
      singleDates={SingleDateCustomInputWrapper.singleDates}
      setOpenDatePicker={SingleDateCustomInputWrapper.setOpenDatePicker}
    />
  );
}

const CustomDataTable = ({
  columns = [],
  records = [],
  isLoading = false,
  pageSize = 10,
  totalRecords = 0,
  setPageIndex = () => { },
  handleSorting = () => { },
  setSearchArry = () => { },
  searchArr = [],
  onResetAll = () => { },
  onRowSelect = () => { },
  onDownload = () => { },
  showDownload = false,
  isSelectAll = false,
  onNameClick = () => { },
  pageIndex = 0,
  showCreateNew = false,
  onCreateNew = null,
  toolbarActions = [],
  initialSortConfig,
  initialRowSelection = [],
  onResetSelectionRef = null,
}) => {
  const {
    dateRanges,
    singleDates,
    openDatePicker,
    selectedRows,
    sortConfig,
    filter,
    totalPages,
    partialSelectAll,
    deselectedItems,
    setOpenDatePicker,
    handleResetAll,
    handleSort,
    handleSelectAll,
    handleRowSelect,
    handleFilterChange,
    handleDateRangeChange,
    handleSingleDateChange,
    resetSelection,
  } = useCustomDataTable({
    columns,
    initialSortConfig,
    records,
    pageSize,
    onResetAll,
    onRowSelect,
    handleSorting,
    setSearchArry,
    setPageIndex,
    isSelectAll,
    totalRecords,
    pageIndex,
    initialRowSelection,
    searchArr,
  });

  // Inject latest props into wrapper components via static assignment
  DateRangeCustomInputWrapper.dateRanges = dateRanges;
  DateRangeCustomInputWrapper.setOpenDatePicker = setOpenDatePicker;
  SingleDateCustomInputWrapper.singleDates = singleDates;
  SingleDateCustomInputWrapper.setOpenDatePicker = setOpenDatePicker;

  // Expose resetSelection function via ref if provided
  if (onResetSelectionRef) {
    onResetSelectionRef.current = resetSelection;
  }

  return (
    <div>
      <DataTableToolbar
        onResetAll={handleResetAll}
        selectedRowsCount={selectedRows?.length || 0}
        toolbarActions={[
          ...toolbarActions,
          ...(showCreateNew ? [{
            id: 'create-new',
            label: 'Create New',
            onClick: onCreateNew,
            icon: <Icon icon="add" size="small" />,
            variant: "secondary",
            isDark: true
          }] : []),
          ...(showDownload ? [{
            id: 'download',
            label: 'Download',
            onClick: onDownload,
            icon: <Icon icon="download" size="small" />,
            variant: "outlined",
            isDark: true,
            requiresSelection: true
          }] : [])
        ]}
      />
      <SelectionSummary selectedCount={
        partialSelectAll
          ? totalRecords - deselectedItems.size
          : isSelectAll
            ? totalRecords
            : (selectedRows?.length || 0)
      } />
      <TableContainer
        component={Paper}
        className={`table-scroll-container${records.length > 5 ? ' table-scroll-container--minheight' : ''}`}
      >
        <Table className="dark-mode-table table-min-width">
          <TableHead>
            <DataTableHead
              columns={columns}
              sortConfig={sortConfig}
              handleSort={handleSort}
              isSelectAll={isSelectAll}
              records={records}
              selectedRows={selectedRows}
              handleSelectAll={handleSelectAll}
              partialSelectAll={partialSelectAll}
              deselectedItems={deselectedItems}
            />
            <DataTableFilterRow
              columns={columns}
              dateRanges={dateRanges}
              singleDates={singleDates}
              openDatePicker={openDatePicker}
              filter={filter}
              setOpenDatePicker={setOpenDatePicker}
              handleDateRangeChange={handleDateRangeChange}
              handleSingleDateChange={handleSingleDateChange}
              handleFilterChange={handleFilterChange}
              DateRangeCustomInput={DateRangeCustomInputWrapper}
              SingleDateCustomInput={SingleDateCustomInputWrapper}
            />
          </TableHead>
          <DataTableBody
            columns={columns}
            records={records}
            isLoading={isLoading}
            selectedRows={selectedRows}
            handleRowSelect={handleRowSelect}
            handleResetAll={handleResetAll}
            onNameClick={onNameClick}
            EmptyState={EmptyState}
            ShimmerTableRow={ShimmerTableRow}
            partialSelectAll={partialSelectAll}
            deselectedItems={deselectedItems}
          />
        </Table>
      </TableContainer>
      <div className="sticky-pagination">
        <DataTablePagination
          totalPages={Number.isInteger(totalPages) && totalPages > 0 ? totalPages : 1}
          page={Number.isInteger(pageIndex) && pageIndex >= 0 ? pageIndex + 1 : 1}
          handlePageChange={(value) => {
            setPageIndex(value - 1);
          }}
        />
      </div>

    </div>
  );
}

export default CustomDataTable;
